import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { CustomAutoComplete } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Typography, Box } from "@mui/material";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { CurrentTemplate } from "./CurrentTemplate";
import { OverrideTemplate } from "./OverrideTemplate";
import { Preview } from "./Preview";
import { infoStyle } from "services/constants";
import { AnimatedConnector } from "components/CustomComponents/Stepper";
import { CustomSftCategoryStepIcon } from "pages/HR/Shift/Configuration/ShiftCategory/Component/ShiftCategoryOptions";
import { showToast } from "services/utils/Status";
import { customGetAPI } from "services/utils/CustomAPI";
import { customAPI } from "services/utils/CustomAPI";
import { themes } from "services/constants";
import { defaultAllSelection } from "services/constants";
import { useSalaryRevise } from "./SalaryReviseContext";
import dayjs from "dayjs";

const steps = [{ label: "Revision" }, { label: "Preview" }];

export const SalaryRevision = ({ reCallApi }) => {
  const { domain, topbarCompanyId, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const { result: salaryTemplates } = useFetch(
    `${domain}hrzone/salary/salary_template/get-all-template/?company_id=${topbarCompanyId}`,
  );
  const { result: revisionTypes } = useFetch(
    `${domain}hrzone/salary/salary_revision/salary-revision-choices/`,
  );
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}`,
  );
  const {
    newTemplateData,
    setNewTemplateData,
    editData,
    setEditData,
    prefillDetails,
    setPrefillDetails,
    showReprocessDialog,
    setShowReprocessDialog,
    setDataupdated,
    setHandleButtonLoder,
    handleButtonLoader,
    tabValue,
    setTabValue,
    previewData,
    setPreviewData,
    setTriggerOnce,
    setClearValues,
    setGetDate,
  } = useSalaryRevise();

  const [templateBasedUsersData, setTemplateBasedUsersData] = useState([]);
  const [getTemplateBasedUsers, setGetTemplateBasedUsers] = useState(false);
  const [currentTemplateData, setCurrentTemplateData] = useState([]);
  const formRef = useRef(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    unregister,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    shouldUnregister: true,
    defaultValues: {
      current_template_changed: false,
      new_template_changed: false,
      current_template_id: "",
      current_template: "",
      new_template: "",
      new_template_id: "",
      revision_type: "Template",
      template_name: "",
      employee_name: "",
      employee_id: "",
      department_id: defaultAllSelection,
      company_id: topbarCompanyId,
    },
  });

  const currentTemplateId = watch("current_template_id");
  const isCurrentTemplateChanged = watch("current_template_changed");
  const isUserTemplateChanged = watch("user_template_changed");
  const userId = watch("employee_id");
  const newTemplateId = watch("new_template_id");
  const isNewTemplateChanged = watch("new_template_changed");
  const templateId = watch("template_id");
  const revisionType = watch("revision_type");
  const departmentId = watch("department_id");

  const { result: membersData, setLoad: reCallMemberApi } = useFetch(
    `${domain}get-department-based-user/?department_id=${watch("department_id") ?? defaultAllSelection}&user_id=${userDetails.id}`,
  );

  useEffect(() => {
    if (revisionType !== "Template") {
      const fieldsToClear = ["current_template", "new_template"];
      fieldsToClear.forEach((field) => {
        unregister(field);
        clearErrors(field);
      });
    } else if (revisionType === "Template") {
      const fieldsForDepartment = [
        "selection_based_on",
        "department",
        "employee_name",
        "template_name",
      ];

      const fieldsForTemplate = [
        "selection_based_on",
        "template_name",
        "employee_name",
      ];

      const fieldsToHandle =
        watch("selection_based_on") === "Department"
          ? fieldsForTemplate
          : fieldsForDepartment;

      fieldsToHandle.forEach((field) => {
        unregister(field);
        clearErrors(field);
      });
    }
  }, [clearErrors, revisionType, unregister, watch]);

  const handleCancelClose = () => {
    setCurrentTemplateData([]);
    setNewTemplateData([]);
    setPreviewData([]);
    setEditData([]);
    setShowReprocessDialog(false);
    setHandleButtonLoder(false);
    setTabValue(0);
  };

  const onSubmit = async (data) => {
    setHandleButtonLoder(true);
    if (tabValue === 0) {
      formRef.current?.submit();
      setTriggerOnce(true);
      setHandleButtonLoder(false);
      setGetDate(true);
      return;
    }

    if (tabValue === 1) {
      if (!previewData || !newTemplateData) return;

      const isPreviewValid = await previewData?.validate?.();
      if (!isPreviewValid) {
        setHandleButtonLoder(false);
        return;
      }
      const isEdit = Boolean(editData?.revision?.revision_id);
      const payload = {
        ...(data?.revision_type === "Employee" && {
          template_id: templateId,
          department_id: departmentId,
          user_id: userId,
          selection_based_on: data?.selection_based_on,
        }),
        ...(isEdit && {
          revision_id: editData?.revision?.revision_id,
        }),
        revision_based_on: data?.revision_type,
        company_id: topbarCompanyId,
        ...(data?.revision_type !== "Employee" && {
          applicable_for: previewData?.applicable_for,
        }),
        reason: previewData?.reason,
        old_salary_template:
          data?.revision_type === "Employee"
            ? data?.selection_based_on === "Department"
              ? currentTemplateId
              : templateId
            : currentTemplateId,
        new_salary_template:
          data?.revision_type === "Employee"
            ? newTemplateData?.new_template_id
            : newTemplateId,
        salary_type: newTemplateData?.salary_type,
        salary_basis: newTemplateData?.salary_basis,
        effective_from: previewData?.start_date.format("YYYY-MM-DD"),
        salary: newTemplateData?.salary,
        total_earnings: newTemplateData?.total_salary,
        total_deductions: newTemplateData?.total_deductions,
        over_ride: newTemplateData?.is_override_template,
        net_salary: newTemplateData?.net_salary,
        total_employer_contribution:
          newTemplateData?.total_employer_contribution,
        total_reimbursement: newTemplateData?.total_reimbursement,
        total_ctc: newTemplateData?.total_ctc,
        components: [
          newTemplateData?.earnings,
          newTemplateData?.deduction,
          newTemplateData?.employer_contribution,
          newTemplateData?.reimbursement,
        ]
          .flat()
          .filter(Boolean),
      };
      const url = `${domain}hrzone/salary/salary_revision/salary-revision/`;

      const method = isEdit ? "PUT" : "POST";
      const response = await customAPI(url, method, payload);
      if (response.ok) {
        showToast("success", response?.responseDetails?.detail);
        reCallApi(true);
        setShowReprocessDialog(false);
        setNewTemplateData([]);
        setPreviewData([]);
        setEditData([]);
        setHandleButtonLoder(false);
        setTabValue(0);
      } else {
        showToast("error", response?.error);
        setHandleButtonLoder(false);
      }
    }
  };

  const handleRevisionType = (event, data) => {
    if (data) {
      setValue("revision_type", data?.label);
      setClearValues(true);
      setCurrentTemplateData([]);
      setNewTemplateData([]);
      setPreviewData([]);
      setEditData([]);
    }
  };

  const handleCurrentTemplate = (event, data) => {
    if (data) {
      setValue("current_template", data?.template_name);
      setValue("current_template_id", data?.id);
      setValue("new_template", "");
      setValue("new_template_id", "");
      setValue("current_template_changed", true);
    }
  };

  const handleNewSalaryTemplate = (event, data) => {
    if (data) {
      setValue("new_template", data?.template_name);
      setValue("new_template_id", data?.id);
      setValue("new_template_changed", true);
    }
  };

  const handleTemplateName = (event, data) => {
    if (data) {
      setValue("template_name", data?.template_name);
      setValue("template_id", data?.id);
      setGetTemplateBasedUsers(true);
      setValue("employee_name", "");
      setValue("employee_id", "");
    }
  };

  const handleSelectionBasedOn = (event, data) => {
    if (data) {
      setValue("selection_based_on", data?.label);
      setClearValues(true);
      setValue("department", "");
      setValue("department_id", "");
      setValue("new_template", "");
      setValue("new_template_id", "");
      setValue("employee_name", "");
      setValue("employee_id", "");
      setValue("template_name", "");
      setValue("template_id", "");
      setCurrentTemplateData([]);
      setNewTemplateData([]);
      setPreviewData([]);
      setEditData([]);
      if (watch("selection_based_on") === "Department") {
        unregister(["template_name", "template_id"]);
        clearErrors("template_name", "template_id");
      } else {
        unregister(["department", "department_id"]);
        clearErrors("department", "department_id");
      }
    }
  };

  const handleDepartment = (event, data) => {
    if (data) {
      setValue("department", data?.department_name);
      setValue("department_id", data?.id);
      reCallMemberApi(true);
      setValue("employee_name", "");
      setValue("employee_id", "");
    }
  };

  const handleEmployeeName = (event, data) => {
    if (data) {
      setValue("employee_name", data?.user_name);
      setValue("employee_id", data?.user_id);
      setValue("user_template_changed", true);
    }
  };

  useEffect(() => {
    const getUserTemplateDetails = async () => {
      const res = await customGetAPI(
        `${domain}hrzone/salary/salary_table/employee-current-salary-template/?user_id=${watch(
          "employee_id",
        )}`,
      );

      if (res.ok) {
        setCurrentTemplateData(res?.responseDetails?.salary_template_data);
        setValue(
          "current_template_id",
          res?.responseDetails?.salary_template_data?.template_id,
        );
        setValue("user_template_changed", false);
      } else {
        showToast("error", res?.message);
      }
    };

    if (userId && isUserTemplateChanged) {
      getUserTemplateDetails();
    }
  }, [
    currentTemplateId,
    domain,
    isUserTemplateChanged,
    setCurrentTemplateData,
    setValue,
    userId,
    watch,
  ]);

  useEffect(() => {
    const getCurrentTemplateDetails = async () => {
      const res = await customGetAPI(
        `${domain}hrzone/salary/salary_template/salary-template-list/?template_id=${watch(
          "current_template_id",
        )}`,
      );

      if (res.ok) {
        setCurrentTemplateData(res?.responseDetails);
        setValue("current_template_changed", false);
      } else {
        showToast("error", res?.message);
      }
    };

    if (currentTemplateId && isCurrentTemplateChanged) {
      getCurrentTemplateDetails();
    }
  }, [
    currentTemplateId,
    domain,
    isCurrentTemplateChanged,
    setCurrentTemplateData,
    setValue,
    watch,
  ]);

  useEffect(() => {
    const getNewTemplateDetails = async () => {
      const res = await customGetAPI(
        `${domain}hrzone/salary/salary_template/salary-template-list/?template_id=${watch(
          "new_template_id",
        )}`,
      );

      if (res.ok) {
        setNewTemplateData(res?.responseDetails);
        setValue("new_template_changed", false);
        setDataupdated(true);
      } else {
        showToast("error", res?.message);
      }
    };

    if (newTemplateId && isNewTemplateChanged) {
      getNewTemplateDetails();
    }
  }, [
    newTemplateId,
    isNewTemplateChanged,
    domain,
    watch,
    setValue,
    setNewTemplateData,
    setDataupdated,
  ]);

  useEffect(() => {
    const getUserBasedTemplate = async () => {
      const res = await customGetAPI(
        `${domain}hrzone/salary/salary_template/get-template-based-current-user/?template_id=${
          templateId
        }`,
      );

      if (res.ok) {
        setTemplateBasedUsersData(res?.responseDetails);
        setGetTemplateBasedUsers(false);
      } else {
        showToast("error", res?.message);
      }
    };

    if (getTemplateBasedUsers && templateId) {
      getUserBasedTemplate();
    }
  }, [domain, getTemplateBasedUsers, templateId, watch]);

  useEffect(() => {
    if (prefillDetails && editData?.length !== 0) {
      setValue("revision_type", editData?.revision?.revision_based_on);
      setValue("current_template", editData?.previous_salary?.template_name);
      setValue("current_template_id", editData?.previous_salary?.template_id);
      setValue("new_template", editData?.new_salary?.template_name);
      setValue("new_template_id", editData?.new_salary?.template_id);
      setValue("selection_based_on", editData?.revision?.selection_based_on);
      setValue("employee_name", editData?.revision?.employee_name);
      setValue("employee_id", editData?.revision?.user_id);
      setValue("template_name", editData?.revision?.template_name);
      setValue("template_id", editData?.revision?.template_id);
      setValue("department_id", editData?.revision?.department_id);
      setValue("department", editData?.revision?.department_name);
      setValue("is_override_template", editData?.new_salary?.over_ride);
      setCurrentTemplateData({
        ...editData?.previous_salary,
        total_salary: editData?.previous_salary?.salary,
        override_count: editData?.revision?.override_count,
        non_override_count: editData?.revision?.non_override_count,
        total_employee_count: editData?.revision?.total_employee_count,
      });
      setNewTemplateData(editData?.new_salary);
      setPreviewData({
        start_date: dayjs(editData?.new_salary?.effective_from),
        applicable_for: editData?.revision?.applicable_for,
        reason: editData?.revision?.reason,
      });
      setDataupdated(true);
      setPrefillDetails(false);
      reCallMemberApi(true);
    }
  }, [
    editData,
    prefillDetails,
    reCallMemberApi,
    setCurrentTemplateData,
    setDataupdated,
    setNewTemplateData,
    setPrefillDetails,
    setPreviewData,
    setValue,
    watch,
  ]);

  return (
    <React.Fragment>
      <ConfirmationDialog
        open={showReprocessDialog}
        onClose={handleCancelClose}
        onConfirm={handleSubmit(onSubmit)}
        title={"Salary Revision"}
        showChildren={true}
        cancelText={tabValue === 1 ? "Previous" : "Cancel"}
        confirmText={
          tabValue === 1
            ? editData?.revision?.revision_id
              ? "Update"
              : "Revise"
            : "Next"
        }
        defaultWidth={1600}
        onPrevious={() => {
          setTabValue(0);
          setDataupdated(true);
        }}
        disableAction={handleButtonLoader}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
          }}
        >
          <Box></Box>
          <Box>
            <Mui.Stepper
              activeStep={tabValue}
              alternativeLabel
              connector={<AnimatedConnector />}
              sx={{ mb: 2 }}
            >
              {steps.map((step, index) => (
                <Mui.Step key={step.label}>
                  <Mui.StepLabel
                    StepIconComponent={CustomSftCategoryStepIcon}
                    StepIconProps={{ variant: "salaryRevision" }}
                    sx={{
                      "& .MuiStepLabel-label.Mui-active": {
                        color: "#00D4AA",
                        fontWeight: "bold",
                      },
                      "& .MuiStepLabel-label.Mui-completed": {
                        color: "#00D4AA",
                      },
                    }}
                  >
                    {step.label}
                  </Mui.StepLabel>
                </Mui.Step>
              ))}
            </Mui.Stepper>
          </Box>
          <Box></Box>
        </Box>

        <Mui.Divider />
        {tabValue === 0 && (
          <>
            <Box sx={{ mb: 3 }}></Box>
            <Box
              sx={{
                mb: 2,
                pointerEvents: editData?.revision?.revision_id
                  ? "none"
                  : "auto",
                opacity: editData?.revision?.revision_id ? 0.8 : 1,
              }}
            >
              <Typography fontWeight={600}>Revision based on</Typography>
              <Typography {...infoStyle}>
                Select the salary revision basis.
              </Typography>
              <CustomAutoComplete
                dataList={
                  revisionTypes?.revision_based_on?.map((obj) => ({
                    ...obj,
                    label: obj.label,
                  })) || []
                }
                rules={{
                  ...register("revision_type", {
                    required: "Revision type is required",
                  }),
                }}
                selectedValue={watch("revision_type")}
                updateDataFunction={handleRevisionType}
                placeHolder="Select Revision type"
                errorMessage={
                  errors?.revision_type?.message
                    ? errors?.revision_type?.message
                    : ""
                }
                textBoxError={errors?.revision_type ? true : false}
              />
            </Box>
            <Mui.Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                mb: 3,
                justifyContent: "space-between",
                pointerEvents: editData?.revision?.revision_id
                  ? "none"
                  : "auto",
                opacity: editData?.revision?.revision_id ? 0.8 : 1,
              }}
            >
              {watch("revision_type") === "Template" ? (
                <>
                  <Mui.Box sx={{ flex: 1, minWidth: 280 }}>
                    <Mui.Typography sx={{ fontWeight: 600 }}>
                      Current Template
                    </Mui.Typography>
                    <Mui.Typography {...infoStyle}>
                      Choose the type of salary structure.
                    </Mui.Typography>
                    <CustomAutoComplete
                      dataList={salaryTemplates.map((obj) => ({
                        ...obj,
                        label: obj.template_name,
                      }))}
                      rules={{
                        ...register("current_template", {
                          required: "Current template is required",
                        }),
                      }}
                      selectedValue={watch("current_template")}
                      updateDataFunction={handleCurrentTemplate}
                      placeHolder="Select Current Template"
                      errorMessage={
                        errors?.current_template?.message
                          ? errors?.current_template?.message
                          : ""
                      }
                      textBoxError={errors?.current_template ? true : false}
                    />
                  </Mui.Box>
                  <Mui.Box sx={{ flex: 1, minWidth: 280 }}>
                    <Mui.Typography sx={{ fontWeight: 600 }}>
                      New Template Name
                    </Mui.Typography>
                    <Mui.Typography {...infoStyle}>
                      Choose the type of salary structure.
                    </Mui.Typography>
                    <CustomAutoComplete
                      dataList={salaryTemplates
                        .filter((obj) => obj.id !== currentTemplateId)
                        .map((obj) => ({
                          ...obj,
                          label: obj.template_name,
                        }))}
                      rules={{
                        ...register("new_template", {
                          required: "New Salary template is required",
                        }),
                      }}
                      selectedValue={watch("new_template")}
                      updateDataFunction={handleNewSalaryTemplate}
                      placeHolder="Select new salary template"
                      errorMessage={
                        errors?.new_template?.message
                          ? errors?.new_template?.message
                          : ""
                      }
                      textBoxError={errors?.new_template ? true : false}
                    />
                  </Mui.Box>
                </>
              ) : (
                <>
                  <Mui.Box sx={{ flex: 1, minWidth: 280 }}>
                    <Mui.Typography sx={{ fontWeight: 600 }}>
                      Selection Based On
                    </Mui.Typography>
                    <Mui.Typography {...infoStyle}>
                      Select the section based on department or employee
                    </Mui.Typography>
                    <CustomAutoComplete
                      dataList={
                        revisionTypes?.selection_based_on?.map((obj) => ({
                          ...obj,
                          label: obj.label,
                        })) || []
                      }
                      rules={{
                        ...register("selection_based_on", {
                          required: "Selection based on is required",
                        }),
                      }}
                      selectedValue={watch("selection_based_on")}
                      updateDataFunction={handleSelectionBasedOn}
                      placeHolder="Select selection based on"
                      errorMessage={
                        errors?.selection_based_on?.message
                          ? errors?.selection_based_on?.message
                          : ""
                      }
                      textBoxError={errors?.selection_based_on ? true : false}
                    />
                  </Mui.Box>
                  {watch("selection_based_on") === "Department" ? (
                    <Mui.Box sx={{ flex: 1, minWidth: 280 }}>
                      <Mui.Typography sx={{ fontWeight: 600 }}>
                        Department
                      </Mui.Typography>
                      <Mui.Typography {...infoStyle}>
                        Select the department
                      </Mui.Typography>
                      <CustomAutoComplete
                        dataList={
                          departmentData?.map((obj) => ({
                            ...obj,
                            label: obj.department_name,
                          })) || []
                        }
                        rules={{
                          ...register("department", {
                            required: "Deparment is required",
                          }),
                        }}
                        selectedValue={watch("department")}
                        updateDataFunction={handleDepartment}
                        placeHolder="Select new salary template"
                        errorMessage={
                          errors?.department?.message
                            ? errors?.department?.message
                            : ""
                        }
                        textBoxError={errors?.department ? true : false}
                      />
                    </Mui.Box>
                  ) : (
                    <Mui.Box sx={{ flex: 1, minWidth: 280 }}>
                      <Mui.Typography sx={{ fontWeight: 600 }}>
                        Template
                      </Mui.Typography>
                      <Mui.Typography {...infoStyle}>
                        Select the template
                      </Mui.Typography>
                      <CustomAutoComplete
                        dataList={salaryTemplates.map((obj) => ({
                          ...obj,
                          label: obj.template_name,
                        }))}
                        rules={{
                          ...register("template_name", {
                            required:
                              watch("selection_based_on") === "Template"
                                ? "Template name is required"
                                : false,
                          }),
                        }}
                        selectedValue={watch("template_name")}
                        updateDataFunction={handleTemplateName}
                        placeHolder="Select template name"
                        errorMessage={
                          errors?.template_name?.message
                            ? errors?.template_name?.message
                            : ""
                        }
                        textBoxError={errors?.template_name ? true : false}
                      />
                    </Mui.Box>
                  )}
                  <Mui.Box sx={{ flex: 1, minWidth: 280 }}>
                    <Mui.Typography sx={{ fontWeight: 600 }}>
                      Employee
                    </Mui.Typography>
                    <Mui.Typography {...infoStyle}>
                      Select the employee for the revision
                    </Mui.Typography>
                    <CustomAutoComplete
                      dataList={
                        watch("selection_based_on") === "Template"
                          ? (templateBasedUsersData?.map((obj) => ({
                              ...obj,
                              label: obj.user_name,
                            })) ?? [])
                          : membersData
                              ?.filter((obj) => obj.user_name !== "All")
                              .map((obj) => ({
                                ...obj,
                                label: obj.user_name,
                              })) || []
                      }
                      rules={{
                        ...register("employee_name", {
                          required: "Employee name is required",
                        }),
                      }}
                      selectedValue={watch("employee_name")}
                      updateDataFunction={handleEmployeeName}
                      placeHolder="Select employee name"
                      errorMessage={
                        errors?.employee_name?.message
                          ? errors?.employee_name?.message
                          : ""
                      }
                      textBoxError={errors?.employee_name ? true : false}
                    />
                  </Mui.Box>
                </>
              )}
            </Mui.Box>
            <Box>
              <Mui.Divider />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                mt: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  border: `1px solid ${themes?.redColor}`,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                }}
              >
                <CurrentTemplate
                  revisionBasedOn={watch("revision_type")}
                  isEdit={editData?.revision?.revision_id}
                  currentTemplateData={currentTemplateData}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  border: `1px solid ${themes?.greenColor}`,
                  borderRadius: 2,
                }}
              >
                <OverrideTemplate
                  ref={formRef}
                  getCompanyId={watch("company_id") ?? topbarCompanyId}
                  salaryTemplates={salaryTemplates}
                  revisionBasedOn={watch("revision_type")}
                  templateName={
                    watch("selection_based_on") === "Department"
                      ? ""
                      : watch("template_name")
                  }
                />
              </Box>
            </Box>
          </>
        )}

        {tabValue === 1 && (
          <>
            <Preview
              currentTemplateData={currentTemplateData}
              revisionBasedOn={watch("revision_type")}
              userId={userId}
            />
          </>
        )}
      </ConfirmationDialog>
    </React.Fragment>
  );
};
